import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Loader2, GraduationCap, Calendar, Clock, Users,
  CheckCircle2, Sparkles,
} from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CoachingRegisterModal from './CoachingRegisterModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORT_COLOR = {
  football: '#16a34a', padel: '#0ea5e9', pickleball: '#f59e0b',
  cricket: '#dc2626', other: '#6b7280',
};
const sportColor = (s) => SPORT_COLOR[s] || '#6b7280';
const sportLabel = (s, other) => {
  if (s === 'other') return other || 'Other';
  const map = { football: 'Football', padel: 'Padel', pickleball: 'Pickleball', cricket: 'Cricket' };
  return map[s] || s;
};
const levelLabel = (l) => {
  const map = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', all_levels: 'All levels' };
  return map[l] || l;
};
const DAY_FULL = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const fmtDays = (days = []) => days.map(d => DAY_FULL[d] || d).join(' & ');
const fmtTime12 = (t) => {
  if (!t || !t.includes(':')) return t || '';
  const [h, m] = t.split(':');
  const hi = parseInt(h, 10);
  const suf = hi < 12 ? 'AM' : 'PM';
  const h12 = (hi % 12) || 12;
  return `${h12}:${m} ${suf}`;
};
const fmtDuration = (mins) => mins === 60 ? '1 hour' : mins === 90 ? '1.5 hrs' : mins === 120 ? '2 hrs' : `${mins} min`;

const normPhone = (s) => (s || '').replace(/\D/g, '').slice(0, 10);

const EventDetailPublic = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useCustomerAuth();
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registerPackage, setRegisterPackage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const phone = user?.phone ? normPhone(user.phone) : '';
      const url = `${API_URL}/api/coaching/public/${slug}${phone ? `?phone=${phone}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) {
        setPr(null);
      } else {
        setPr(await res.json());
      }
    } catch {
      setPr(null);
    } finally {
      setLoading(false);
    }
  }, [slug, user?.phone]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-gray-400" /></div>;
  }
  if (!pr) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <p className="text-sm text-gray-500 mb-3">Programme not found</p>
        <button onClick={() => navigate('/coaching')} className="font-semibold text-[#0A2C93] underline text-sm">Back to coaching</button>
      </div>
    );
  }

  const viewer = pr.viewer_registration;
  const packages = pr.packages || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Sticky header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/coaching')} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg" data-testid="detail-back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-bold truncate">{pr.title}</h1>
            <p className="text-[11px] text-gray-500 -mt-0.5">{pr.venue_name}</p>
          </div>
        </div>
      </div>

      {/* Banner */}
      <div className="aspect-video relative max-w-3xl mx-auto" style={{ backgroundColor: sportColor(pr.sport) }} data-testid="detail-banner">
        {pr.banner_image_url ? (
          <img src={`${API_URL}${pr.banner_image_url}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GraduationCap className="h-12 w-12 text-white/70" />
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-4 space-y-4">
        {/* Title + badges */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 leading-tight" data-testid="detail-title">{pr.title}</h2>
          <div className="flex items-center gap-1.5 flex-wrap mt-2">
            <span
              className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide text-white"
              style={{ backgroundColor: sportColor(pr.sport) }}
              data-testid="detail-sport-badge"
            >
              {sportLabel(pr.sport, pr.other_sport_name)}
            </span>
            <span
              className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-[#0A2C93]/10 text-[#0A2C93]"
              data-testid="detail-level-badge"
            >
              {levelLabel(pr.level)}
            </span>
            {pr.status === 'cancelled' && <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-red-100 text-red-700">Cancelled</span>}
            {pr.status === 'completed' && <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-gray-100 text-gray-600">Ended</span>}
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-xl border p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-700">{pr.venue_name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-700">{fmtDays(pr.days_of_week)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-700">{fmtTime12(pr.start_time)} · {fmtDuration(pr.session_duration_minutes)} per session</span>
          </div>
        </div>

        {/* Description */}
        {pr.description && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">About this programme</h3>
            <p className="text-sm text-gray-700 whitespace-pre-line">{pr.description}</p>
          </div>
        )}

        {/* Coach section — only rendered when coach.name is present */}
        {pr.coach && pr.coach.name && (
          <div className="bg-white rounded-xl border p-4 flex items-start gap-3" data-testid="coach-section">
            {pr.coach.photo_url ? (
              <img src={`${API_URL}${pr.coach.photo_url}`} alt={pr.coach.name} className="h-14 w-14 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="h-14 w-14 rounded-full bg-[#0A2C93]/10 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="h-6 w-6 text-[#0A2C93]" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-500 uppercase">Coach</p>
              <p className="text-sm font-semibold text-gray-900">{pr.coach.name}</p>
              {pr.coach.bio && <p className="text-xs text-gray-600 mt-1 leading-relaxed">{pr.coach.bio}</p>}
            </div>
          </div>
        )}

        {/* Registered banner */}
        {viewer && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-2"
            data-testid="registered-banner"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900">You are registered for {viewer.package_label}</p>
              <p className="text-xs text-emerald-800 mt-0.5">We will contact you with further details before your first session.</p>
            </div>
          </div>
        )}

        {/* Packages */}
        <div data-testid="packages-section">
          <h3 className="text-base font-bold text-gray-900 mb-2">Choose a package</h3>
          <div className="space-y-3">
            {packages.map(pkg => {
              const isRegistered = viewer && viewer.package_id === pkg.id;
              const isOpen = pkg.status === 'open';
              const spots = pkg.spots_left ?? 0;
              const full = isOpen && spots <= 0;
              const sessions = pkg.sessions_total;
              return (
                <div
                  key={pkg.id}
                  className={`rounded-xl border bg-white p-4 ${pkg.status === 'cancelled' ? 'opacity-60' : ''} ${isRegistered ? 'border-emerald-400 bg-emerald-50/50' : ''}`}
                  data-testid={`pkg-card-${pkg.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-gray-900">{pkg.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{sessions} sessions</p>
                    </div>
                    <p className="text-xl font-bold text-gray-900 flex-shrink-0" data-testid={`pkg-price-${pkg.id}`}>
                      {pkg.price > 0 ? `₹${pkg.price}` : 'Free'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {pkg.status === 'cancelled'
                        ? 'Cancelled'
                        : full
                        ? 'Full'
                        : `${spots} spots remaining`}
                    </span>
                  </div>

                  {isRegistered ? (
                    <button
                      disabled
                      className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5 cursor-default"
                      data-testid={`pkg-registered-${pkg.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Registered
                    </button>
                  ) : full || !isOpen || pr.status !== 'open' ? (
                    <button
                      disabled
                      className="w-full py-2.5 rounded-lg bg-gray-100 text-gray-500 text-sm font-semibold cursor-not-allowed"
                      data-testid={`pkg-disabled-${pkg.id}`}
                    >
                      {pkg.status === 'cancelled' ? 'Cancelled' : pr.status !== 'open' ? 'Unavailable' : 'Full'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setRegisterPackage(pkg)}
                      className="w-full py-2.5 rounded-lg bg-[#0A2C93] text-white text-sm font-semibold hover:bg-[#082273]"
                      data-testid={`pkg-register-${pkg.id}`}
                    >
                      Register
                    </button>
                  )}
                </div>
              );
            })}
            {packages.length === 0 && (
              <div className="bg-white rounded-xl border p-6 text-center text-sm text-gray-500">
                <Sparkles className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                No packages available.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Register modal */}
      {registerPackage && (
        <CoachingRegisterModal
          programme={pr}
          pkg={registerPackage}
          onClose={() => setRegisterPackage(null)}
          onRegistered={() => { setRegisterPackage(null); load(); }}
        />
      )}
    </div>
  );
};

export default EventDetailPublic;
